import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Link } from '@tanstack/react-router'
import { ArrowRight, LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { fadeInUp, viewportOnce } from '@/lib/animations'
import { H2 } from '@/components/ui/typography'
import Scribble31 from '@/assets/32.svg?react'
import Star15 from '@/assets/15.svg?react'
import React from 'react'

type CTAVariant = 'subtle' | 'primary' | 'dark'

const staggerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.2 },
  },
}

interface CTASectionProps {
  /**
   * i18n key prefix — component reads {root}.title, {root}.description,
   * {root}.{buttonKey}. Individual title/description/buttonLabel props
   * override the lookup (useful when content is dynamically interpolated).
   */
  translationKeyRoot?: string
  /** Overrides {root}.title — pass a pre-translated string for dynamic content. */
  title?: string
  /** Overrides {root}.description */
  description?: string
  /** Overrides {root}.{buttonKey} */
  buttonLabel?: string
  /** Overrides {root}.{noteKey} */
  note?: string
  /** Sub-key for the button label. Defaults to 'button'. */
  buttonKey?: string
  /** Sub-key for an optional note below the button. Defaults to 'note'. */
  noteKey?: string
  buttonTo: string
  buttonParams?: Record<string, string>
  /**
   * Icon rendered inside the button.
   * Subtle variant: no icon by default.
   * Primary/dark variants: defaults to ArrowRight.
   */
  buttonIcon?: LucideIcon
  onButtonClick?: () => void
  variant?: CTAVariant
  /**
   * Extra button(s) rendered beside the primary (subtle variant only).
   * Useful when the CTA needs a secondary action (e.g. "Call Now" anchor).
   */
  secondaryButton?: React.ReactNode
  /**
   * When false, renders only the inner card without a <section> wrapper.
   * Use this when embedding inside an existing page section. Default: true.
   */
  asSection?: boolean
  /** i18next namespace for the translationKeyRoot. Defaults to 'marketing'. */
  ns?: string
}

export function CTASection({
  translationKeyRoot: root,
  title: titleProp,
  description: descriptionProp,
  buttonLabel: buttonLabelProp,
  note: noteProp,
  buttonKey = 'button',
  noteKey = 'note',
  buttonTo,
  buttonParams,
  buttonIcon,
  onButtonClick,
  variant = 'subtle',
  secondaryButton,
  asSection = true,
  ns = 'marketing',
}: CTASectionProps) {
  const Icon = buttonIcon ?? (variant !== 'subtle' ? ArrowRight : undefined)
  const { t } = useTranslation(ns)

  const title = titleProp ?? (root ? t(`${root}.title`) : '')
  const description = descriptionProp ?? (root ? t(`${root}.description`) : '')
  const buttonLabel = buttonLabelProp ?? (root ? t(`${root}.${buttonKey}`) : '')
  const note = noteProp ?? (root ? t(`${root}.${noteKey}`, { defaultValue: '' }) : '')

  if (variant === 'subtle') {
    const card = (
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="p-8 text-center">
          <h2 className="text-2xl font-bold mb-3">{title}</h2>
          <p className="text-muted-foreground mb-6">{description}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button size="lg" asChild>
              <Link
                to={buttonTo as any}
                params={buttonParams}
                className="flex items-center gap-2"
                onClickCapture={onButtonClick}
              >
                {Icon && <Icon className="w-5 h-5" />}
                {buttonLabel}
              </Link>
            </Button>
            {secondaryButton}
          </div>
        </CardContent>
      </Card>
    )

    if (!asSection) return card

    return (
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">{card}</div>
      </section>
    )
  }

  if (variant === 'primary') {
    return (
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={viewportOnce}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          >
            <Card className="bg-primary text-primary-foreground border-0 rounded-3xl">
              <CardContent className="p-8 text-center">
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={staggerVariants}
                >
                  <motion.div variants={fadeInUp}>
                    <H2 className="mb-4">{title}</H2>
                  </motion.div>
                  <motion.p
                    className="text-xl mb-8 text-primary-foreground/90 max-w-xl mx-auto"
                    variants={fadeInUp}
                  >
                    {description}
                  </motion.p>
                  <motion.div className="relative inline-block" variants={fadeInUp}>
                    <Scribble31 className="absolute w-20 h-16 text-primary-foreground/50 -left-24 top-1/4 -translate-y-1/2 rotate-90 scale-y-[-1]" />
                    <Star15 className="absolute w-8 h-8 text-primary-foreground/50 -right-12 top-1/4 -translate-y-1/2" />
                    <Button
                      size="lg"
                      variant="secondary"
                      className="text-lg px-8 py-6 group"
                      asChild
                    >
                      <Link
                        to={buttonTo as any}
                        params={buttonParams}
                        className="flex items-center gap-2"
                        onClickCapture={onButtonClick}
                      >
                        {buttonLabel}
                        <Icon className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </motion.div>
                  {note && (
                    <motion.p
                      className="mt-6 text-sm text-primary-foreground/80"
                      variants={fadeInUp}
                    >
                      {note}
                    </motion.p>
                  )}
                </motion.div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>
    )
  }

  // dark variant
  return (
    <section className="py-20 px-4">
      <div className="container mx-auto max-w-5xl">
        <motion.div
          className="relative bg-foreground rounded-3xl p-16 text-center text-background overflow-hidden"
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={viewportOnce}
          transition={{ duration: 0.7, ease: 'easeOut' }}
        >
          <div className="absolute inset-0 bg-gradient-to-tr from-primary/30 to-transparent pointer-events-none" />
          <motion.div
            className="relative z-10 space-y-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerVariants}
          >
            <motion.div variants={fadeInUp}>
              <H2 className="text-background">{title}</H2>
            </motion.div>
            <motion.p
              className="text-background/60 max-w-xl mx-auto text-lg"
              variants={fadeInUp}
            >
              {description}
            </motion.p>
            <motion.div className="relative inline-block" variants={fadeInUp}>
              <Scribble31 className="absolute w-20 h-16 text-background/30 -left-24 top-1/4 -translate-y-1/2 rotate-90 scale-y-[-1] hidden sm:block" />
              <Star15 className="absolute w-8 h-8 text-background/30 -right-12 top-1/4 -translate-y-1/2 hidden sm:block" />
              <Button size="lg" className="text-lg px-10 py-6 group" asChild>
                <Link
                  to={buttonTo as any}
                  params={buttonParams}
                  className="flex items-center gap-2"
                  onClickCapture={onButtonClick}
                >
                  {buttonLabel}
                  <Icon className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </motion.div>
            {note && (
              <motion.p
                className="mt-2 text-sm text-background/60"
                variants={fadeInUp}
              >
                {note}
              </motion.p>
            )}
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}
